import { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { productsCartState } from '../../../../../../../store/batchTicket/atom';
import {
  AssetDefaultOrSelectedTypeMap,
  SubmitUpdateProps,
} from '../../../../../../../store/product/types';
import { EquityListItemTypes } from '../../../components/Asset/products/Equity/ListItem';

export const useEquityAssetController = (
  offer: AssetDefaultOrSelectedTypeMap['equity'],
  submitUpdate?: (data: SubmitUpdateProps) => Promise<boolean>
) => {
  const equityOffersSelected = useRecoilValue(productsCartState).equity;

  const itemBasketSelectedIndex = equityOffersSelected.items.findIndex(
    (item) => item.productIdNormalized === offer.productIdNormalized
  );

  const defaultQuantity =
    equityOffersSelected?.items[itemBasketSelectedIndex]?.orderQuantity ||
    offer.orderQuantity ||
    offer.quote.lotSize;
  const defaultValue =
    equityOffersSelected?.items[itemBasketSelectedIndex]?.orderValue ||
    offer.orderValue ||
    offer.priceNormalized;
  const defaultOrderType =
    (
      equityOffersSelected?.items[itemBasketSelectedIndex] as {
        orderType?: EquityListItemTypes.OrderType;
      }
    )?.orderType ||
    ((offer as { orderType?: EquityListItemTypes.OrderType }).orderType ?? 'Limit');

  const [isFocusedAmountInput, setIsFocusedAmountInput] = useState(false);
  const [amountItem, setAmountItem] = useState(defaultQuantity);
  const [priceItem, setPriceItem] = useState(defaultValue);
  const [orderType, setOrderType] = useState<EquityListItemTypes.OrderType>(defaultOrderType);

  const syncPriceWithLastForMarket = useCallback(() => {
    const lastPrice = offer.quote?.lastPrice ?? 0;
    setPriceItem(Number(lastPrice.toFixed(2)));
  }, [offer.quote?.lastPrice]);

  const handleChangeOrderType = useCallback(
    (next: EquityListItemTypes.OrderType) => {
      setOrderType(next);
      if (next === 'Market') {
        syncPriceWithLastForMarket();
      }
    },
    [syncPriceWithLastForMarket]
  );

  useEffect(() => {
    if (orderType !== 'Market') {
      return;
    }
    syncPriceWithLastForMarket();
  }, [orderType, offer.quote?.lastPrice, syncPriceWithLastForMarket]);

  const totalPrice = amountItem * priceItem;

  const totalPriceIsInvalid = totalPrice === 0 || totalPrice > 1000000;

  const handleChangeAmountItem = useCallback(
    async (value: number) => {
      if (submitUpdate) {
        const response = await submitUpdate({ amount: value, price: priceItem });

        setAmountItem((state) => (!totalPriceIsInvalid && response ? value : state));
      } else {
        setAmountItem(value);
      }
    },
    [priceItem, submitUpdate, totalPriceIsInvalid]
  );

  const handleChangePriceItem = useCallback(
    async (value: number) => {
      if (orderType === 'Market') {
        return;
      }
      const valueFixed = Number(value.toFixed(2));

      if (submitUpdate) {
        const response = await submitUpdate({ price: valueFixed, amount: amountItem });

        setPriceItem((state) => (!totalPriceIsInvalid && response ? valueFixed : state));
      } else {
        setPriceItem(valueFixed);
      }
    },
    [amountItem, orderType, submitUpdate, totalPriceIsInvalid]
  );

  const lotSize = offer.quote.lotSize || 1;
  const fractionalLotSize = offer.fractionalQuote
    ? offer.fractionalQuote.lotSize
    : offer.quote.lotSize;
  const cautionStepper =
    amountItem <= lotSize && offer.fractionalQuote ? fractionalLotSize : lotSize;

  return {
    amountItem,
    totalPrice,
    priceItem,
    handleChangeAmountItem,
    handleChangePriceItem,
    totalPriceIsInvalid,
    cautionStepper,
    fractionalLotSize,
    isFocusedAmountInput,
    setIsFocusedAmountInput,
    orderType,
    handleChangeOrderType,
  };
};

export type EquityController = ReturnType<typeof useEquityAssetController>;
